import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { forwardRef, useRef } from "react";
import { c as clsx } from "../../../chunks/clsx-DgYk2OaC.js";
import { Icon } from "../../../foundations/icons/Icon.js";
import { Text } from "../../../foundations/typography/Text/Text.js";
import { FocusRing } from "../../../library/react-aria/FocusRing.js";
import { Spinner } from "../../feedback/Spinner/Spinner.js";
import { omitProp } from "./helpers/remove-attribute.js";
import { isButton, isLink } from "./helpers/typeguard.js";
import { $ as $701a24aa0da5b062$export$ea18c227d4417cc3 } from "../../../chunks/useButton-BIR5vkbu.js";
import { $ as $3ef42575df84b30b$export$9d1611c77c2fe928 } from "../../../chunks/mergeProps-bpc4gVdj.js";
import { $ as $65484d02dcb7eb3e$export$457c3d6518dd4c6f } from "../../../chunks/filterDOMProps-D2C6R0DK.js";
import { $ as $f6c31cce2adf654f$export$45712eceda6fad21, a as $ea8dcbcb9ea1b556$export$9a302a45f65d0572, b as $ea8dcbcb9ea1b556$export$7e924b3091a3bd18, c as $ea8dcbcb9ea1b556$export$13aea1a3cb5e3f1f } from "../../../chunks/usePress-BBilPT3f.js";
import { $ as $f645667febf57a63$export$4c014de7c8940b4c } from "../../../chunks/useFocusable-Rgl20men.js";
function $298d61e98472621b$export$dcf14c9974fe2767(props, ref) {
  let { elementType = "a", onPress, onPressStart, onPressEnd, onClick, isDisabled, ...otherProps } = props;
  let linkProps = {};
  if (elementType !== "a") linkProps = {
    role: "link",
    tabIndex: !isDisabled ? 0 : void 0
  };
  let { focusableProps } = $f645667febf57a63$export$4c014de7c8940b4c(props, ref);
  let { pressProps, isPressed } = $f6c31cce2adf654f$export$45712eceda6fad21({
    onPress,
    onPressStart,
    onPressEnd,
    onClick,
    isDisabled,
    ref
  });
  let domProps = $65484d02dcb7eb3e$export$457c3d6518dd4c6f(otherProps, {
    labelable: true
  });
  let interactionHandlers = $3ef42575df84b30b$export$9d1611c77c2fe928(focusableProps, pressProps);
  let router = $ea8dcbcb9ea1b556$export$9a302a45f65d0572();
  let routerLinkProps = $ea8dcbcb9ea1b556$export$7e924b3091a3bd18(props);
  return {
    isPressed,
    linkProps: $3ef42575df84b30b$export$9d1611c77c2fe928(domProps, routerLinkProps, {
      ...interactionHandlers,
      ...linkProps,
      "aria-disabled": isDisabled || void 0,
      "aria-current": props["aria-current"],
      onClick: (e) => {
        var _pressProps_onClick;
        (_pressProps_onClick = pressProps.onClick) === null || _pressProps_onClick === void 0 ? void 0 : _pressProps_onClick.call(pressProps, e);
        $ea8dcbcb9ea1b556$export$13aea1a3cb5e3f1f(e, router, props.href, props.routerOptions);
      }
    })
  };
}
const button = "_button_slktx_1";
const buttonChildren = "_buttonChildren_slktx_21";
const buttonIcon = "_buttonIcon_slktx_27";
const buttonContent = "_buttonContent_slktx_32";
const spinnerElement = "_spinnerElement_slktx_40";
const buttonIsLoading = "_buttonIsLoading_slktx_72";
const buttonFullWidth = "_buttonFullWidth_slktx_165";
const buttonEllipsis = "_buttonEllipsis_slktx_171";
const onlyGhostMediumButton = "_onlyGhostMediumButton_slktx_200";
const isAlignedOnText = "_isAlignedOnText_slktx_207";
const styles = {
  button,
  buttonChildren,
  buttonIcon,
  buttonContent,
  spinnerElement,
  "button-small": "_button-small_slktx_46",
  "button-medium": "_button-medium_slktx_52",
  "button-large": "_button-large_slktx_58",
  "button-primary": "_button-primary_slktx_66",
  buttonIsLoading,
  "button-primary--pressed": "_button-primary--pressed_slktx_79",
  "spinner-primary": "_spinner-primary_slktx_85",
  "button-secondary": "_button-secondary_slktx_90",
  "button-secondary--pressed": "_button-secondary--pressed_slktx_103",
  "spinner-secondary": "_spinner-secondary_slktx_109",
  "button-outline": "_button-outline_slktx_114",
  "button-outline--pressed": "_button-outline--pressed_slktx_127",
  "spinner-outline": "_spinner-outline_slktx_133",
  "button-ghost": "_button-ghost_slktx_138",
  "button-ghost--pressed": "_button-ghost--pressed_slktx_152",
  "spinner-ghost": "_spinner-ghost_slktx_158",
  buttonFullWidth,
  buttonEllipsis,
  onlyGhostMediumButton,
  isAlignedOnText
};
const sizeMap = {
  small: { typography: "text-caption-head", iconSize: 20 },
  medium: { typography: "text-body-head", iconSize: 24 },
  large: { typography: "text-body-head", iconSize: 24 }
};
const ButtonContent = ({
  children,
  icon,
  size,
  isLoading,
  isLoadingDescription,
  variant
}) => {
  const spinnerClasses = clsx(styles.spinnerElement, styles["spinner-" + variant]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs("span", { className: styles.buttonChildren, children: [
      icon && /* @__PURE__ */ jsx(
        Icon,
        {
          variant: icon,
          width: sizeMap[size].iconSize,
          height: sizeMap[size].iconSize,
          className: styles.buttonIcon,
          "aria-hidden": true
        }
      ),
      /* @__PURE__ */ jsx(Text, { component: "span", variant: sizeMap[size].typography, className: styles.buttonContent, children })
    ] }),
    isLoading && /* @__PURE__ */ jsx(Spinner, { size: "small", className: spinnerClasses, description: isLoadingDescription })
  ] });
};
const ButtonComponent = forwardRef(
  ({
    children,
    className,
    hasTextWrap,
    icon,
    isFullWidth,
    isLoading,
    isLoadingDescription,
    onPress,
    size = "medium",
    variant = "primary",
    ...props
  }, ref) => {
    const buttonRef = useRef(null);
    const { buttonProps, isPressed } = $701a24aa0da5b062$export$ea18c227d4417cc3({ onPress, preventFocusOnPress: true }, buttonRef);
    const buttonClasses = clsx(
      styles.button,
      styles[`button-${variant}`],
      styles[`button-${size}`],
      { [styles["button-" + variant + "--pressed"]]: isPressed },
      { [styles.buttonIsLoading]: isLoading },
      { [styles.buttonEllipsis]: hasTextWrap },
      { [styles.buttonFullWidth]: isFullWidth },
      className
    );
    return /* @__PURE__ */ jsx(FocusRing, { children: /* @__PURE__ */ jsx(
      "button",
      {
        ...buttonProps,
        ref,
        "aria-busy": isLoading,
        className: buttonClasses,
        disabled: isLoading,
        ...props,
        children: /* @__PURE__ */ jsx(
          ButtonContent,
          {
            icon,
            size,
            isLoading,
            isLoadingDescription,
            variant,
            children
          }
        )
      }
    ) });
  }
);
const LinkComponent = forwardRef(
  ({
    children,
    className,
    hasTextWrap,
    icon,
    isLoading,
    isLoadingDescription,
    size = "medium",
    variant = "primary",
    ...props
  }, ref) => {
    const anchorRef = useRef(null);
    const { linkProps, isPressed } = $298d61e98472621b$export$dcf14c9974fe2767({}, anchorRef);
    const buttonClasses = clsx(
      styles.button,
      styles["button-" + variant],
      styles["button-" + size],
      { [styles["button-" + variant + "--pressed"]]: isPressed },
      { [styles.buttonIsLoading]: isLoading },
      { [styles.buttonEllipsis]: hasTextWrap },
      className
    );
    return /* @__PURE__ */ jsx(
      "a",
      {
        ...linkProps,
        ref,
        "aria-busy": isLoading ? "true" : void 0,
        className: buttonClasses,
        ...props,
        children: /* @__PURE__ */ jsx(
          ButtonContent,
          {
            icon,
            size,
            isLoading,
            variant,
            isLoadingDescription,
            children
          }
        )
      }
    );
  }
);
const Button = forwardRef(
  (props, ref) => {
    if (isButton(props)) {
      const filteredProps = omitProp(props, "component");
      return /* @__PURE__ */ jsx(ButtonComponent, { ref, ...filteredProps });
    }
    if (isLink(props)) {
      const filteredProps = omitProp(props, "component");
      return /* @__PURE__ */ jsx(LinkComponent, { ref, ...filteredProps });
    }
    return null;
  }
);
const ButtonPrimary = forwardRef(({ component = "button", ...props }, ref) => {
  return /* @__PURE__ */ jsx(Button, { component, variant: "primary", ref, ...props });
});
const ButtonSecondary = forwardRef(({ component = "button", ...props }, ref) => {
  return /* @__PURE__ */ jsx(Button, { ...props, ref, component, variant: "secondary" });
});
const ButtonOutline = forwardRef(({ component = "button", ...props }, ref) => {
  return /* @__PURE__ */ jsx(Button, { ...props, ref, component, variant: "outline" });
});
const ButtonGhost = forwardRef(({ isAlignedOnText: isAlignedOnText2, component = "button", ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    Button,
    {
      ...props,
      component,
      variant: "ghost",
      size: "medium",
      ref,
      className: clsx(
        styles.onlyGhostMediumButton,
        { [styles.isAlignedOnText]: isAlignedOnText2 },
        props.className
      )
    }
  );
});
export {
  Button,
  ButtonGhost,
  ButtonOutline,
  ButtonPrimary,
  ButtonSecondary
};
